<template>
  <div class="file-upload-component">
    <!-- Drop zone -->
    <div
      class="drop-zone"
      :class="{ 'drag-over': isDragging, 'has-files': files.length > 0 }"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
      @click="triggerFileInput"
    >
      <input
        ref="fileInput"
        type="file"
        :accept="accept"
        :multiple="maxFiles > 1"
        class="file-input"
        @change="onFileSelect"
      >
      <div class="drop-zone-content">
        <feather-icon
          icon="UploadCloudIcon"
          size="40"
          class="upload-icon"
        />
        <p class="drop-text">
          {{ $t('DEBTIA_FILE_DROP') }}
        </p>
        <p class="file-info">
          {{ acceptedTypesText }} ({{ $t('DEBTIA_FILE_MAX_SIZE', { size: maxSize }) }})
        </p>
        <p
          v-if="maxFiles > 1"
          class="file-limit"
        >
          {{ $t('DEBTIA_FILE_LIMIT', { count: maxFiles }) }}
        </p>
      </div>
    </div>

    <!-- File list -->
    <div
      v-if="files.length > 0"
      class="file-list"
    >
      <div
        v-for="(file, index) in files"
        :key="index"
        class="file-item"
      >
        <div class="file-icon">
          <feather-icon
            :icon="getFileIcon(file.type)"
            size="20"
          />
        </div>
        <div class="file-details">
          <div class="file-name">
            {{ file.name }}
          </div>
          <div class="file-meta">
            <span class="file-type">{{ getFileTypeLabel(file.type) }}</span>
            <span class="file-size">{{ formatFileSize(file.size) }}</span>
          </div>
        </div>
        <b-button
          variant="flat-danger"
          size="sm"
          class="remove-btn"
          @click.stop="removeFile(index)"
        >
          <feather-icon
            icon="XIcon"
            size="16"
          />
        </b-button>
      </div>
    </div>

    <!-- Error message -->
    <div
      v-if="errorMessage"
      class="error-message"
    >
      <feather-icon
        icon="AlertCircleIcon"
        size="16"
        class="mr-50"
      />
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue'

export default {
  name: 'FileUpload',
  components: {
    BButton,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    maxFiles: {
      type: Number,
      default: 5,
    },
    accept: {
      type: String,
      default: '.jpg,.jpeg,.png,.pdf,.doc,.docx',
    },
    maxSize: {
      type: Number,
      default: 5, // MB
    },
  },
  data() {
    return {
      isDragging: false,
      errorMessage: '',
    }
  },
  computed: {
    files() {
      return this.value || []
    },
    acceptedTypesText() {
      const types = this.accept.split(',').map(t => t.trim().replace('.', '').toUpperCase())
      return types.join(', ')
    },
    acceptedMimeTypes() {
      const mimeMap = {
        '.jpg': 'image/jpeg',
        '.jpeg': 'image/jpeg',
        '.png': 'image/png',
        '.pdf': 'application/pdf',
        '.doc': 'application/msword',
        '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      }
      return this.accept.split(',').map(ext => mimeMap[ext.trim()] || ext.trim())
    },
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    onDragOver() {
      this.isDragging = true
    },
    onDragLeave() {
      this.isDragging = false
    },
    onDrop(e) {
      this.isDragging = false
      const droppedFiles = Array.from(e.dataTransfer.files)
      this.addFiles(droppedFiles)
    },
    onFileSelect(e) {
      const selectedFiles = Array.from(e.target.files)
      this.addFiles(selectedFiles)
      // Reset input so same file can be selected again
      e.target.value = ''
    },
    addFiles(newFiles) {
      this.errorMessage = ''

      // Check max files limit
      if (this.files.length + newFiles.length > this.maxFiles) {
        this.errorMessage = this.$t('DEBTIA_FILE_MAX_ERROR', { count: this.maxFiles })
        return
      }

      const validFiles = newFiles.filter(file => {
        // Validate file type
        if (!this.isValidFileType(file)) {
          this.errorMessage = this.$t('DEBTIA_FILE_TYPE_ERROR', { type: this.getFileExtension(file.name), allowed: this.acceptedTypesText })
          return false
        }

        // Validate file size
        if (file.size > this.maxSize * 1024 * 1024) {
          this.errorMessage = this.$t('DEBTIA_FILE_SIZE_ERROR', { name: file.name, size: this.maxSize })
          return false
        }

        return true
      })

      if (validFiles.length > 0) {
        this.$emit('input', [...this.files, ...validFiles])
      }
    },
    removeFile(index) {
      const newFiles = [...this.files]
      newFiles.splice(index, 1)
      this.$emit('input', newFiles)
      this.errorMessage = ''
    },
    isValidFileType(file) {
      const extension = this.getFileExtension(file.name).toLowerCase()
      const acceptedExtensions = this.accept.split(',').map(ext => ext.trim().toLowerCase())
      return acceptedExtensions.includes(`.${extension}`) || this.acceptedMimeTypes.includes(file.type)
    },
    getFileExtension(filename) {
      return filename.split('.').pop() || ''
    },
    getFileIcon(mimeType) {
      if (mimeType.includes('image')) return 'ImageIcon'
      if (mimeType.includes('pdf')) return 'FileTextIcon'
      if (mimeType.includes('word') || mimeType.includes('document')) return 'FileIcon'
      return 'FileIcon'
    },
    getFileTypeLabel(mimeType) {
      if (mimeType.includes('image/jpeg')) return 'JPEG'
      if (mimeType.includes('image/png')) return 'PNG'
      if (mimeType.includes('pdf')) return 'PDF'
      if (mimeType.includes('msword')) return 'DOC'
      if (mimeType.includes('wordprocessingml')) return 'DOCX'
      return mimeType.split('/').pop()?.toUpperCase() || 'FILE'
    },
    formatFileSize(bytes) {
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },
  },
}
</script>

<style lang="scss" scoped>
.file-upload-component {
  .drop-zone {
    border: 2px dashed rgba(0, 0, 0, 0.15);
    border-radius: 0.75rem;
    padding: 2rem;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
    background: rgba(255, 255, 255, 0.5);

    &:hover {
      border-color: #088c76;
      background: rgba(8, 140, 118, 0.05);
    }

    &.drag-over {
      border-color: #088c76;
      background: rgba(8, 140, 118, 0.1);
      border-style: solid;
    }

    &.has-files {
      padding: 1rem;
    }

    .file-input {
      display: none;
    }

    .drop-zone-content {
      .upload-icon {
        color: #088c76;
        margin-bottom: 0.75rem;
      }

      .drop-text {
        font-weight: 600;
        color: #5e5873;
        margin-bottom: 0.25rem;
      }

      .file-info {
        font-size: 0.85rem;
        color: #6e6b7b;
        margin-bottom: 0;
      }

      .file-limit {
        font-size: 0.8rem;
        color: #a0a0a0;
        margin-bottom: 0;
        margin-top: 0.25rem;
      }
    }
  }

  .file-list {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .file-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    background: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 0.5rem;

    .file-icon {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      background: rgba(8, 140, 118, 0.1);
      color: #088c76;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .file-details {
      flex: 1;
      min-width: 0;

      .file-name {
        font-weight: 500;
        color: #5e5873;
        font-size: 0.95rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .file-meta {
        display: flex;
        gap: 0.75rem;
        font-size: 0.8rem;
        color: #a0a0a0;

        .file-type {
          background: rgba(0, 0, 0, 0.06);
          padding: 0.1rem 0.4rem;
          border-radius: 0.25rem;
          font-weight: 500;
        }
      }
    }

    .remove-btn {
      flex-shrink: 0;
      padding: 0.25rem;

      &:hover {
        background: rgba(234, 84, 85, 0.1);
      }
    }
  }

  .error-message {
    display: flex;
    align-items: center;
    margin-top: 0.75rem;
    padding: 0.75rem 1rem;
    background: rgba(234, 84, 85, 0.1);
    border: 1px solid rgba(234, 84, 85, 0.2);
    border-radius: 0.5rem;
    color: #ea5455;
    font-size: 0.9rem;
  }
}
</style>
